import { useEffect, useRef } from 'react';
import type { GenerationSlot } from '../../../contexts/WidgetContext';
import { resolveIs3D } from '../../../utils/mediaTypeDetection';

/**
 * Extracts the primary image URL from a GenerationSlot for preloading.
 * For video/audio/3D slots returns null (not preloadable as images).
 *
 * 3D is excluded on the URL as well as the content type: a GLB is tens of MB
 * and can never decode as an image, so preloading one costs the whole model
 * download for nothing. Same URL fallback as the render branches in
 * `GenerationSlot` / `MediaPreviewModal`, for rows with a stale content type.
 */
function getPreloadUrl(slot: GenerationSlot): string | null {
  if (
    slot.contentType?.startsWith('video') ||
    slot.contentType?.startsWith('audio')
  ) {
    return null;
  }
  const url = slot.originalUrl || slot.previewUrl || null;
  // Per-asset: a texture/preview PNG inside a 3D generation IS preloadable —
  // only the model file itself must never go through `new Image()`.
  if (resolveIs3D(slot.contentType, url)) return null;
  return url;
}

/**
 * Preloads adjacent images in the lightbox so navigation is instant.
 *
 * When lightboxIndex changes, preloads images at index-1 and index+1
 * using the browser's Image constructor (which populates the HTTP cache).
 * For multi-image slots, preloads only the first image of each adjacent slot.
 */
export function useImagePreloader(
  allSlots: GenerationSlot[],
  lightboxIndex: number | null,
) {
  // Keep refs to avoid creating unnecessary Image objects on re-renders
  const preloadedUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (lightboxIndex === null || allSlots.length === 0) {
      // Clear cache when lightbox closes to prevent unbounded growth
      if (lightboxIndex === null) {
        preloadedUrls.current.clear();
      }
      return;
    }

    const indicesToPreload = [lightboxIndex - 1, lightboxIndex + 1];

    for (const idx of indicesToPreload) {
      // Skip out-of-bounds indices (no wrapping — matches navigator behavior)
      if (idx < 0 || idx >= allSlots.length) continue;
      // Don't preload the current slot (already loaded)
      if (idx === lightboxIndex) continue;

      const slot = allSlots[idx];
      if (!slot) continue;

      const url = getPreloadUrl(slot);
      if (url && !preloadedUrls.current.has(url)) {
        preloadedUrls.current.add(url);
        const img = new Image();
        img.src = url;
      }
    }
  }, [lightboxIndex, allSlots]);
}

// Export for testing
export { getPreloadUrl };
